import { useMemo, useState, useCallback } from 'react';
import { Database, KeyRound } from 'lucide-react';

import { AdminSectionCard } from './AdminSectionCard';
import { ConfigCardStatusFooter, type SaveStatus } from './ConfigCardStatusFooter';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusCard } from '@/components/ui/card/StatusCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiClient } from '@/hooks/useApiClient';
import { useApiCall } from '@/hooks/useApiCall';
import { ConfigApiClient, type InfluxConfigResponse } from '../api/ConfigApiClient';

const MIN_PORT = 1;
const MAX_PORT = 65535;

function isValidPort(value: string): boolean {
  if (!/^\d+$/.test(value)) return false;
  const n = Number(value);
  return n >= MIN_PORT && n <= MAX_PORT;
}

type InfluxFields = { host: string; port: string; user: string; password: string; database: string };
const EMPTY_FIELDS: InfluxFields = { host: '', port: '', user: '', password: '', database: '' };

// ---------------------------------------------------------------------------
// InfluxConfigCard — self-contained InfluxDB backend-config section (load / save / reset):
// the signals database read by the Signals page, the live predictions and the machine form.
// ---------------------------------------------------------------------------

export function InfluxConfigCard() {
  const { t } = useTranslation();
  const http = useApiClient();

  const configClient = useMemo(() => (http ? new ConfigApiClient(http) : null), [http]);

  const [fields, setFields] = useState<InfluxFields>(EMPTY_FIELDS);
  const [loaded, setLoaded] = useState<InfluxFields>(EMPTY_FIELDS);
  const [usingDefaults, setUsingDefaults] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  function setField<K extends keyof InfluxFields>(key: K, value: string) {
    setFields((prev) => ({ ...prev, [key]: value }));
    setSaveStatus('idle');
  }

  const { isLoading, error: loadError, retry } = useApiCall<InfluxConfigResponse>({
    apiFn: useCallback(
      async (_signal: AbortSignal) => {
        if (!configClient) {
          return { ok: false, code: 0, name: 'NotReady', message: 'not ready', timestamp: '' };
        }
        const res = await configClient.getInfluxConfig();
        if (!res.ok && res.code === 403) {
          return { ...res, message: t('backendConfig.errors.forbidden') };
        }
        return res;
      },
      [configClient, t]
    ),
    immediate: true,
    onSuccess: (data) => {
      if (data.configured === false) {
        setUsingDefaults(true);
        setFields(EMPTY_FIELDS);
        setLoaded(EMPTY_FIELDS);
      } else {
        setUsingDefaults(false);
        const values: InfluxFields = {
          host: data.host ?? '',
          port: data.port !== undefined ? String(data.port) : '',
          user: data.user ?? '',
          password: data.password ?? '',
          database: data.database ?? '',
        };
        setFields(values);
        setLoaded(values);
      }
    },
  });

  async function handleSave() {
    if (!configClient || !isValidPort(fields.port)) return;
    setSaveStatus('saving');
    setSaveError(null);
    try {
      const res = await configClient.saveInfluxConfig({ ...fields, port: Number(fields.port) });
      if (res.ok) {
        setSaveStatus('ok');
        setLoaded(fields);
        setUsingDefaults(false);
      } else {
        setSaveStatus('error');
        setSaveError(res.code === 403 ? t('backendConfig.errors.forbidden') : (res.message ?? t('backendConfig.errors.generic')));
      }
    } catch {
      setSaveStatus('error');
      setSaveError(t('backendConfig.errors.generic'));
    }
  }

  function handleReset() {
    setFields(loaded);
    setSaveStatus('idle');
    setSaveError(null);
  }

  const canSave =
    fields.host.trim().length > 0 &&
    isValidPort(fields.port) &&
    fields.user.trim().length > 0 &&
    fields.password.trim().length > 0 &&
    fields.database.trim().length > 0 &&
    saveStatus !== 'saving';

  return (
    <>
      {loadError && !isLoading && (
        <StatusCard
          type="error"
          title={t('backendConfig.errors.loadFailed')}
          message={loadError}
          className="mb-2"
        />
      )}

      <AdminSectionCard
        icon={Database}
        title={t('backendConfig.influx.title')}
        description={t('backendConfig.influx.description')}
      >
        <div className="grid gap-5 sm:grid-cols-2">
          {isLoading ? (
            <>
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-9 w-full" />
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <Label htmlFor="influx-host" className="text-sm font-semibold">
                  {t('backendConfig.fields.influxHost.label')}
                </Label>
                <Input
                  id="influx-host"
                  value={fields.host}
                  onChange={(e) => setField('host', e.target.value)}
                  className="font-mono text-sm"
                  placeholder="influx.example.com"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="influx-port" className="text-sm font-semibold">
                  {t('backendConfig.fields.influxPort.label')}
                </Label>
                <Input
                  id="influx-port"
                  type="number"
                  min={MIN_PORT}
                  max={MAX_PORT}
                  value={fields.port}
                  onChange={(e) => setField('port', e.target.value)}
                  className="font-mono text-sm"
                  placeholder="8086"
                  aria-invalid={fields.port.length > 0 && !isValidPort(fields.port)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="influx-user" className="text-sm font-semibold">
                  {t('backendConfig.fields.influxUser.label')}
                </Label>
                <Input
                  id="influx-user"
                  value={fields.user}
                  onChange={(e) => setField('user', e.target.value)}
                  className="font-mono text-sm"
                  placeholder="toollife"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="influx-password" className="flex items-center gap-2 text-sm font-semibold">
                  <KeyRound className="h-3.5 w-3.5 text-primary" aria-hidden />
                  {t('backendConfig.fields.influxPassword.label')}
                </Label>
                <Input
                  id="influx-password"
                  type="password"
                  value={fields.password}
                  onChange={(e) => setField('password', e.target.value)}
                  className="font-mono text-sm"
                  placeholder="••••••••"
                />
              </div>

              <div className="flex flex-col gap-2 sm:col-span-2">
                <Label htmlFor="influx-database" className="text-sm font-semibold">
                  {t('backendConfig.fields.influxDatabase.label')}
                </Label>
                <Input
                  id="influx-database"
                  value={fields.database}
                  onChange={(e) => setField('database', e.target.value)}
                  className="font-mono text-sm"
                  placeholder="he-arc"
                />
              </div>
            </>
          )}
        </div>

        <ConfigCardStatusFooter
          isLoading={isLoading}
          loadError={loadError}
          usingDefaults={usingDefaults}
          saveStatus={saveStatus}
          saveError={saveError}
          savedMessage={t('backendConfig.influx.savedMessage')}
          canSave={canSave}
          onRetry={retry}
          onReset={handleReset}
          onSave={handleSave}
        />
      </AdminSectionCard>
    </>
  );
}
